import {
  effectiveAlaskaHawaiiDeparturePassengers,
  effectiveDomesticSegmentPassengers,
  effectiveInternationalFacilityPassengers,
  legAirExciseBreakdown,
  type LegAirExciseBreakdown,
} from "./calculator";
import { money } from "./money";
import { passengerExciseRouteRuleLabel } from "./passengerExciseRouteRule";
import type { FlightLeg, Rates } from "./types";

function escapeHtml(s: string): string {
  return s
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

function fmtRate(n: number): string {
  return `$${n.toFixed(2)}`;
}

/**
 * One readable cell: which passenger line fired and the arithmetic (no duplicate zero columns).
 */
function q1LegPassengerBasisHtml(leg: FlightLeg, rates: Rates, b: LegAirExciseBreakdown): string {
  const domP = effectiveDomesticSegmentPassengers(leg);
  const akP = effectiveAlaskaHawaiiDeparturePassengers(leg);
  const intlP = effectiveInternationalFacilityPassengers(leg);
  const segs = Math.max(1, leg.segmentsDomestic);
  const lines: string[] = [];

  if (b.internationalFacilityTax > 0) {
    lines.push(
      `<strong>International facility (IRS No. 27)</strong> — ${intlP} × ${fmtRate(rates.internationalFacilityUsdPerPerson)}`
    );
  } else {
    if (b.domesticSegmentTax > 0) {
      const units = segs * domP;
      lines.push(
        `<strong>Domestic segment (IRS No. 26)</strong> — ${segs} seg × ${domP} pax → ${units} × ${fmtRate(rates.domesticSegmentUsdPerPassengerPerSegment)}`
      );
    }
    if (b.alaskaHawaiiDepartureTax > 0) {
      lines.push(
        `<strong>AK/HI departure (IRS No. 27)</strong> — ${akP} × ${fmtRate(rates.alaskaHawaiiDomesticDepartureUsdPerPerson)}`
      );
    }
  }

  if (
    lines.length === 0 &&
    b.internationalFacilityTax === 0 &&
    b.domesticSegmentTax === 0 &&
    b.alaskaHawaiiDepartureTax === 0
  ) {
    if (leg.excludeFrom720) {
      lines.push("<span class=\"muted-small\">Excluded from 720</span>");
    } else if (leg.ruralAirportExempt && domP > 0) {
      lines.push("<span class=\"muted-small\">Rural exempt — no domestic segment tax</span>");
    } else if (passengerExciseRouteRuleLabel(leg) === "Intl–Intl → 0") {
      lines.push("<span class=\"muted-small\">Foreign–foreign — no U.S. passenger lines</span>");
    } else if (domP === 0 && akP === 0 && intlP === 0) {
      lines.push("<span class=\"muted-small\">No passengers on this leg</span>");
    } else {
      lines.push("<span class=\"muted-small\">No tax — check pax fields</span>");
    }
  }

  const manual: string[] = [];
  if ((leg.alaskaHawaiiDomesticDeparturePassengers ?? 0) > 0) {
    manual.push(`<code>akhi_pax</code> ${leg.alaskaHawaiiDomesticDeparturePassengers}`);
  }
  if ((leg.internationalFacilityPassengers ?? 0) > 0) {
    manual.push(`<code>intl_pax</code> ${leg.internationalFacilityPassengers}`);
  }
  if (manual.length > 0) {
    lines.push(`<span class="muted-small">Stored: ${manual.join(" · ")}</span>`);
  }

  return lines.join("<br/>");
}

function passengerTaxOnlyTotal(b: LegAirExciseBreakdown): number {
  return money(
    b.domesticSegmentTax + b.internationalFacilityTax + b.alaskaHawaiiDepartureTax
  );
}

/**
 * Expandable per-leg table for the 2026 Q1 comparison — compact: Rule, how tax was figured, one $ column.
 */
export function renderQ1AkDomesticLegDetailHtml(
  legs: FlightLeg[],
  rates: Rates
): string {
  if (legs.length === 0) return "";
  const sorted = [...legs].sort((a, b) => a.flightDate.localeCompare(b.flightDate));
  let sumPassengerTax = 0;
  const rows = sorted.map((leg) => {
    const b = legAirExciseBreakdown(leg, rates);
    const total = passengerTaxOnlyTotal(b);
    sumPassengerTax = money(sumPassengerTax + total);
    return `<tr>
      <td>${escapeHtml(leg.flightDate)}</td>
      <td class="comparison-q1-route">${escapeHtml(leg.fromIcao)}–${escapeHtml(leg.toIcao)}</td>
      <td class="comparison-q1-rule muted-small">${escapeHtml(passengerExciseRouteRuleLabel(leg))}</td>
      <td class="comparison-q1-basis">${q1LegPassengerBasisHtml(leg, rates, b)}</td>
      <td class="comparison-num comparison-q1-paxtax">${total.toFixed(2)}</td>
    </tr>`;
  });
  return `
    <details class="panel panel-tight comparison-q1-ak-dom-detail">
      <summary>Per-leg passenger taxes — rule + amounts</summary>
      <p class="sheets-hint muted-small"><strong>Rule</strong> is route geometry. <strong>How taxed</strong> lists each passenger line with dollars: usually <em>one</em> of intl / domestic / AK, but <strong>Alaska or Hawaii → contiguous <code>K…</code></strong> (e.g. Anchorage→Seattle) follows Form 720 instructions for <strong>both</strong> IRS No. 26 domestic segment and No. 27 AK/HI departure. <strong>Passenger tax</strong> is the sum of those lines (no 7.5% / charter here).</p>
      <div class="comparison-table-wrap">
        <table class="totals comparison-table comparison-q1-per-leg-simple">
          <thead>
            <tr>
              <th>Date</th>
              <th>Leg</th>
              <th title="Route geometry">Rule</th>
              <th>How taxed</th>
              <th class="comparison-num" title="Domestic segment, international facility, or AK/HI departure (one per leg)">Passenger tax</th>
            </tr>
          </thead>
          <tbody>${rows.join("")}</tbody>
          <tfoot>
            <tr class="comparison-q1-sum-row">
              <td colspan="4"><strong>Total passenger tax</strong> <span class="muted-small">(${sorted.length} leg${sorted.length === 1 ? "" : "s"})</span></td>
              <td class="comparison-num comparison-q1-paxtax"><strong>${sumPassengerTax.toFixed(2)}</strong></td>
            </tr>
          </tfoot>
        </table>
      </div>
    </details>
  `;
}
